import React, { type ReactNode, useEffect, useMemo } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { Text } from '@atlaskit/primitives/compiled';
import SectionMessage from '@atlaskit/section-message';

import { useAnalytics } from '../../../common/ui/analytics';

import messages from './messages';
import type { SuccessPanelProps } from './types';

const getMessage = ({
	invitedEmails,
	requestedEmails,
}: Pick<SuccessPanelProps, 'invitedEmails' | 'requestedEmails'>) => {
	if (invitedEmails.length === 0 && requestedEmails.length === 0) {
		return { message: null, email: null };
	}
	if (invitedEmails.length >= 1 && requestedEmails.length === 0) {
		return {
			message: messages.allInvited,
			email: invitedEmails[0],
		};
	}
	if (invitedEmails.length === 0 && requestedEmails.length >= 1) {
		return {
			message: messages.allRequested,
			email: requestedEmails[0],
		};
	}
	if (invitedEmails.length >= 1 && requestedEmails.length === 1) {
		return {
			message: messages.hybridOneRequested,
			email: requestedEmails[0],
		};
	}
	if (invitedEmails.length === 1 && requestedEmails.length >= 1) {
		return {
			message: messages.hybridOneInvited,
			email: invitedEmails[0],
		};
	}
	return {
		message: messages.hybrid,
		email: null,
	};
};

export const SuccessPanel = ({
	invitedEmails,
	requestedEmails,
	integration,
}: SuccessPanelProps) => {
	const fireAnalyticsEvent = useAnalytics();

	useEffect(() => {
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'inlineInviteSuccess',
			attributes: {
				invitedUsers: invitedEmails.length,
				requestedUsers: requestedEmails.length,
				integration,
			},
		});
	}, [fireAnalyticsEvent, invitedEmails.length, requestedEmails.length, integration]);

	const { message, email } = useMemo(
		() => getMessage({ invitedEmails, requestedEmails }),
		[invitedEmails, requestedEmails],
	);

	if (!message) {
		return null;
	}

	return (
		<SectionMessage appearance="success">
			<Text size="medium" color="color.text.success">
				<FormattedMessage
					{...message}
					values={{
						invitedCount: invitedEmails.length,
						requestedCount: requestedEmails.length,
						email,
						bold: (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>,
					}}
				/>
			</Text>
		</SectionMessage>
	);
};
